import React, { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const emptyProperty = {
  title: "", description: "", location: "Goa", address: "", property_type: "Villa",
  price_per_night: 5000, max_guests: 4, bedrooms: 2, bathrooms: 2,
  amenities: [], images: [], featured: false, is_active: true,
};

const AMENITIES = ["WiFi", "Air Conditioning", "Private Pool", "Kitchen", "Parking", "Beach Access", "Housekeeping", "Sea View", "BBQ", "Garden", "Yoga Deck", "Workspace", "Gym", "Pool", "Balcony", "Coffee Machine", "Breakfast", "Bicycle", "Long-Stay Discount"];

function PropertyDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => { setForm(initial); }, [initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAmenity = (a) => {
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API}${data.url.replace(/^\/api/, "")}`;
      // Actually the backend serves via /api/files/{path}. data.url already includes /api prefix. Rebuild absolute:
      const absoluteUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      set("images", [...form.images, absoluteUrl]);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addImageUrl = () => {
    if (!imageUrl) return;
    set("images", [...form.images, imageUrl.trim()]);
    setImageUrl("");
  };

  const removeImage = (i) => set("images", form.images.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      let saved;
      if (form.id) {
        const { data } = await api.patch(`/properties/${form.id}`, form);
        saved = data;
      } else {
        const { data } = await api.post("/properties", form);
        saved = data;
      }
      toast.success("Property saved");
      onSaved(saved);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="property-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">{form.id ? "Edit property" : "Add property"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="prop-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1" data-testid="prop-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Location</Label>
              <select value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-[hsl(165_37%_17%/0.14)] px-3 bg-white" data-testid="prop-location">
                <option>Goa</option><option>Bangalore</option>
              </select>
            </div>
            <div>
              <Label>Type</Label>
              <select value={form.property_type} onChange={(e) => set("property_type", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-[hsl(165_37%_17%/0.14)] px-3 bg-white" data-testid="prop-type">
                <option>Villa</option><option>Apartment</option><option>Guest House</option><option>Cottage</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1" data-testid="prop-address" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Price/night (₹)</Label>
              <Input type="number" value={form.price_per_night} onChange={(e) => set("price_per_night", Number(e.target.value))} className="mt-1" data-testid="prop-price" />
            </div>
            <div>
              <Label>Max guests</Label>
              <Input type="number" value={form.max_guests} onChange={(e) => set("max_guests", Number(e.target.value))} className="mt-1" data-testid="prop-guests" />
            </div>
            <div>
              <Label>Bedrooms</Label>
              <Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", Number(e.target.value))} className="mt-1" data-testid="prop-bedrooms" />
            </div>
            <div>
              <Label>Bathrooms</Label>
              <Input type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", Number(e.target.value))} className="mt-1" data-testid="prop-bathrooms" />
            </div>
          </div>

          <div>
            <Label>Amenities</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {AMENITIES.map((a) => (
                <button key={a} type="button" onClick={() => toggleAmenity(a)}
                  className={`chip ${form.amenities.includes(a) ? "chip-active" : ""}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Images</Label>
            <div className="mt-2 grid grid-cols-3 md:grid-cols-4 gap-2">
              {form.images.map((src, i) => (
                <div key={i} className="relative aspect-square rounded overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full h-6 w-6 flex items-center justify-center">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="aspect-square rounded border-2 border-dashed border-[hsl(165_37%_17%/0.2)] flex flex-col items-center justify-center text-xs text-[hsl(100_3%_30%)] cursor-pointer hover:bg-[hsl(42_22%_92%)]">
                <Upload className="h-5 w-5 mb-1" />
                {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="prop-image-upload" />
              </label>
            </div>
            <div className="mt-3 flex gap-2">
              <Input placeholder="or paste image URL" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} data-testid="prop-image-url" />
              <Button type="button" variant="outline" onClick={addImageUrl}>Add URL</Button>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="prop-featured" /> Featured
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="prop-active" /> Active
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} className="btn-forest" data-testid="prop-save">Save property</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProperties() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(emptyProperty);

  const load = () => api.get("/properties").then((r) => setItems(r.data));

  useEffect(() => { load(); }, []);

  const onAdd = () => { setEditing({ ...emptyProperty }); setOpen(true); };
  const onEdit = (p) => { setEditing(p); setOpen(true); };
  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success("Property deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif-display text-2xl">Properties ({items.length})</h2>
        <Button onClick={onAdd} className="btn-forest rounded-full" data-testid="admin-add-property">
          <Plus className="h-4 w-4 mr-2" /> Add property
        </Button>
      </div>

      <div className="border border-[hsl(165_37%_17%/0.1)] rounded-md bg-white divide-y divide-[hsl(165_37%_17%/0.08)]">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-property-${p.id}`}>
            <img src={p.images?.[0]} alt="" className="h-16 w-24 rounded object-cover" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium truncate">{p.title}</h3>
                {p.featured && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(165_37%_17%)] text-white">Signature</span>}
                {!p.is_active && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-700">Inactive</span>}
              </div>
              <p className="text-xs text-[hsl(100_3%_30%)] mt-1">
                {p.location} · {p.property_type} · ₹{p.price_per_night.toLocaleString("en-IN")}/night · {p.bedrooms}br
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="icon" variant="outline" onClick={() => onEdit(p)} data-testid={`edit-property-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="outline" onClick={() => onDelete(p)} data-testid={`delete-property-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <PropertyDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}
